import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { SessionStatus, Session } from '../types';

// Date formatting utilities
export const formatDate = (dateString: string): string => {
  try {
    return format(parseISO(dateString), 'MMM dd, yyyy HH:mm');
  } catch {
    return 'Invalid date';
  }
};

export const formatDateShort = (dateString: string): string => {
  try {
    return format(parseISO(dateString), 'MMM dd');
  } catch {
    return 'Invalid date';
  }
};

export const formatTimeAgo = (dateString: string): string => {
  try {
    return formatDistanceToNow(parseISO(dateString), { addSuffix: true });
  } catch {
    return 'Unknown time';
  }
};

// Status utilities
export const getStatusColor = (status: SessionStatus): string => {
  switch (status) {
    case SessionStatus.PRE_CREATED:
      return 'text-indigo-600 bg-indigo-100';
    case SessionStatus.PENDING:
      return 'text-yellow-600 bg-yellow-100';
    case SessionStatus.ACTIVE:
      return 'text-green-600 bg-green-100';
    case SessionStatus.EXPIRED:
      return 'text-red-600 bg-red-100';
    case SessionStatus.COMPLETED:
      return 'text-blue-600 bg-blue-100';
    case SessionStatus.TERMINATED:
      return 'text-gray-600 bg-gray-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

export const getStatusText = (status: SessionStatus): string => {
  switch (status) {
    case SessionStatus.PRE_CREATED:
      return 'Pre-created';
    case SessionStatus.PENDING:
      return 'Pending';
    case SessionStatus.ACTIVE:
      return 'Active';
    case SessionStatus.EXPIRED:
      return 'Expired';
    case SessionStatus.COMPLETED:
      return 'Completed';
    case SessionStatus.TERMINATED:
      return 'Terminated';
    default:
      return 'Unknown';
  }
};

// Number formatting utilities
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat().format(num);
};

export const formatPercentage = (value: number, total: number): string => {
  if (total === 0) return '0%';
  return `${((value / total) * 100).toFixed(1)}%`;
};

// Session utilities
export const getSessionProgress = (session: Session): number => {
  if (session.message_limit === 0) return 0;
  return (session.messages_sent_by_client / session.message_limit) * 100;
};

export const getRemainingMessages = (session: Session): number => {
  return Math.max(0, session.message_limit - session.messages_sent_by_client);
};

export const isSessionExpiringSoon = (session: Session): boolean => {
  const now = new Date();
  const expiresAt = parseISO(session.expires_at);
  const hoursRemaining = (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60);
  return hoursRemaining <= 2 && hoursRemaining > 0;
};

// Validation utilities
export const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

export const validateApiId = (apiId: string): boolean => {
  const num = parseInt(apiId);
  return !isNaN(num) && num > 0;
};

export const validateApiHash = (apiHash: string): boolean => {
  return apiHash.length === 32 && /^[a-f0-9]+$/i.test(apiHash);
};

// Copy to clipboard utility
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
};

// Class name utility (similar to clsx)
export const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};
